import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageSelector from '../components/LanguageSelector';
import { REGISTER_COUNTRIES, SUPPORTED_REGISTER_CODES } from '../data/registerCountries';
import SeoMeta from '../components/SeoMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  // B2B-only pivot (Feb 2026): every new sign-up is a pro account, so
  // company name is now always required.
  const [companyName, setCompanyName] = useState('');
  // French DGAC / EASA UAS operator ID — optional (many one-person pros
  // haven't registered yet, and we don't want to block their sign-up).
  const [uasOperator, setUasOperator] = useState('');
  // Optional "add my first drone" bloc. Collapsed by default so the form
  // stays short for users who want to poke around before configuring.
  const [addDroneOpen, setAddDroneOpen] = useState(false);
  const [droneModel, setDroneModel] = useState('');
  const [droneWeight, setDroneWeight] = useState('');
  const [droneClass, setDroneClass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Landing-page pricing cards deep-link here with `?plan=<id>&promo=<code>`.
  // After a successful register we forward the user straight to /subscription
  // with the same promo so the checkout keeps the discount.
  const desiredPlan = searchParams.get('plan');
  const promoCode = searchParams.get('promo');

  // Pre-fill the country dropdown using the backend's IP geolocation.
  // The call is best-effort and silent on failure — the user can always
  // pick manually. We only auto-select when the detected country is in
  // our 18-country list; otherwise we leave the field empty so the user
  // makes a conscious choice.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/geo/detect-country`, { timeout: 4000 })
      .then((r) => {
        const detected = (r.data?.country || '').toLowerCase();
        if (!cancelled && detected && SUPPORTED_REGISTER_CODES.has(detected)) {
          setCountry(detected);
        }
      })
      .catch(() => { /* silent — the dropdown stays empty */ });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(t('auth.register.passwordRequirements'));
      return;
    }
    if (!country) {
      toast.error(t('auth.register.countryRequired', 'Veuillez sélectionner votre pays de résidence'));
      return;
    }
    if (!companyName.trim()) {
      toast.error(t('auth.register.companyRequired', 'Le nom de votre société est requis pour un compte professionnel'));
      return;
    }

    // Validate the optional drone bloc when the user opened it. We only
    // want to bounce them if they *started* filling it and left required
    // fields blank — the whole point is to keep it optional.
    let firstDrone = null;
    if (addDroneOpen && (droneModel.trim() || droneClass || droneWeight)) {
      const w = parseFloat((droneWeight || '').replace(',', '.'));
      if (!droneModel.trim() || !droneClass || !Number.isFinite(w) || w <= 0) {
        toast.error("Complétez le drone (modèle, classe CE, poids) ou refermez la section.");
        return;
      }
      firstDrone = {
        model_name: droneModel.trim(),
        weight_kg: w,
        ce_class: droneClass,
      };
    }

    setLoading(true);

    try {
      await register({
        name,
        email,
        password,
        country,
        companyName: companyName.trim(),
        uasOperatorNumber: uasOperator.trim() || null,
        firstDrone,
      });
      toast.success(t('common.success'));
      // If the user came from a landing pricing CTA (`?plan=solo&promo=SUMMER`),
      // forward them to the subscription page with the promo pre-applied so
      // Stripe checkout keeps the discount. Otherwise, go to the dashboard.
      if (desiredPlan === 'solo' || desiredPlan === 'solo_annual') {
        const qs = new URLSearchParams({ plan: desiredPlan });
        if (promoCode) qs.set('promo', promoCode);
        navigate(`/subscription?${qs.toString()}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      <SeoMeta title="Inscription" description="Créez votre compte Drone Maps et démarrez avec 3 missions gratuites." noindex />
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Link>
            <LanguageSelector variant="compact" />
          </div>

          <Link to="/" className="inline-flex items-center mb-8" data-testid="register-logo">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-12 w-auto" />
          </Link>

          <h1 className="text-3xl font-bold text-white font-[Barlow] mb-2">{t('auth.register.title')}</h1>
          <p className="text-slate-400 mb-8">{t('auth.register.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-300">{t('auth.register.name')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-300">{t('auth.register.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="country" className="text-slate-300">
                {t('auth.register.country', 'Pays de résidence')}
              </Label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                data-testid="register-country-select"
                className="mt-2 w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 appearance-none cursor-pointer"
              >
                <option value="" disabled>
                  {t('auth.register.countryPlaceholder', '— Sélectionnez votre pays —')}
                </option>
                {REGISTER_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.label}
                  </option>
                ))}
              </select>
            </div>

            {/* B2B pivot: company_name is now always required (the
                amateur/pro selector was retired in Feb 2026). */}
            <div>
              <Label htmlFor="company_name" className="text-slate-300">
                {t('auth.register.companyName', 'Nom de la société')}
              </Label>
              <Input
                id="company_name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('auth.register.companyPlaceholder', 'Ex : Aerial Studio SAS')}
                required
                maxLength={120}
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                data-testid="register-company-name-input"
              />
            </div>

            {/* Optional UAS operator number (DGAC, ENAC, LBA, …). Kept
                free-text on purpose because national formats diverge. */}
            <div>
              <Label htmlFor="uas_operator" className="text-slate-300">
                Numéro d&apos;exploitant UAS <span className="text-slate-500 text-xs">(facultatif)</span>
              </Label>
              <Input
                id="uas_operator"
                type="text"
                value={uasOperator}
                onChange={(e) => setUasOperator(e.target.value)}
                placeholder="Ex : FRA1a2b3c4d5e6f7"
                maxLength={32}
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                data-testid="register-uas-operator-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Format DGAC ({`FRA + 13 caractères`}) ou équivalent EASA. Ajoutable plus tard depuis votre profil.
              </p>
            </div>

            {/* Optional first drone — collapsible. Encoding it here saves
                the "empty fleet" round-trip that used to kill activation. */}
            <div className="rounded-md border border-slate-700 bg-slate-800/40">
              <button
                type="button"
                onClick={() => setAddDroneOpen((v) => !v)}
                data-testid="register-add-drone-toggle"
                className="w-full flex items-center justify-between text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800/70 transition rounded-md"
                aria-expanded={addDroneOpen}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg" aria-hidden>🚁</span>
                  <span>
                    <span className="font-semibold">J&apos;ai déjà un drone</span>
                    <span className="text-slate-500"> — l&apos;ajouter maintenant (facultatif, ~1 min)</span>
                  </span>
                </span>
                <span className="text-slate-500 text-lg">{addDroneOpen ? '−' : '+'}</span>
              </button>

              {addDroneOpen && (
                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-700" data-testid="register-drone-fields">
                  <div>
                    <Label htmlFor="drone_model" className="text-slate-300 text-xs">Modèle</Label>
                    <Input
                      id="drone_model"
                      type="text"
                      value={droneModel}
                      onChange={(e) => setDroneModel(e.target.value)}
                      placeholder="DJI Mavic 3 Pro"
                      maxLength={80}
                      className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                      data-testid="register-drone-model"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="drone_class" className="text-slate-300 text-xs">Classe CE</Label>
                      <select
                        id="drone_class"
                        value={droneClass}
                        onChange={(e) => setDroneClass(e.target.value)}
                        data-testid="register-drone-class"
                        className="mt-1 w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 appearance-none cursor-pointer"
                      >
                        <option value="">— Classe —</option>
                        <option value="C0">C0 (&lt; 250 g)</option>
                        <option value="C1">C1 (&lt; 900 g)</option>
                        <option value="C2">C2 (&lt; 4 kg)</option>
                        <option value="C3">C3 (&lt; 25 kg)</option>
                        <option value="C4">C4 (&lt; 25 kg, sans classe)</option>
                        <option value="C5">C5 (STS 01)</option>
                        <option value="C6">C6 (STS 02)</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="drone_weight" className="text-slate-300 text-xs">Poids (kg)</Label>
                      <Input
                        id="drone_weight"
                        type="text"
                        inputMode="decimal"
                        value={droneWeight}
                        onChange={(e) => setDroneWeight(e.target.value)}
                        placeholder="0,958"
                        className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                        data-testid="register-drone-weight"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">{t('auth.register.password')}</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500 pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">{t('auth.register.passwordRequirements')}</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold"
              data-testid="register-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.register.submit')}
            </Button>
          </form>

          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>{t('landing.hero.freeMissions')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>{t('landing.hero.noCard')}</span>
            </div>
          </div>

          <p className="mt-8 text-center text-slate-400 text-sm">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="text-sky-400 hover:text-sky-300 font-medium">
              {t('auth.register.login')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1595835338177-2184b375827c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwcXVhZGNvcHRlciUyMGRyb25lJTIwZmx5aW5nfGVufDB8fHx8MTc3MTI0ODMyOXww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-slate-900/60" />
      </div>
    </div>
  );
}
